import { addMonths } from '../reports/customise';

/**
 * Piece 24, decisions 0216 and 11020: the recurring template. The job
 * `generateRecurringDrafts` runs at the hour `accounting.recurringRunHour`
 * names, seeded at 5, and raises a Draft Bill or a Draft Manual Journal
 * (decision 11520), for each period that has come due.
 */
export const GENERATE_RECURRING_DRAFTS_JOB = 'generateRecurringDrafts';
export const GENERATE_RECURRING_DRAFTS_HOUR_KEY = 'accounting.recurringRunHour';
export const GENERATE_RECURRING_DRAFTS_DEFAULT_HOUR = 5;

/** 🔨 One template raises at most this many periods in one run, so a template left for years cannot flood the Bills. */
export const RECURRING_MAX_PERIODS_PER_RUN = 12;

export const RECURRING_SOURCE_TYPES = ['Bill', 'JournalEntry'] as const;
export type RecurringSourceTypeValue = (typeof RECURRING_SOURCE_TYPES)[number];

export const RECURRING_FREQUENCIES = ['Monthly', 'Quarterly', 'Annual'] as const;
export type RecurringFrequencyValue = (typeof RECURRING_FREQUENCIES)[number];

const MONTHS: Record<RecurringFrequencyValue, number> = { Monthly: 1, Quarterly: 3, Annual: 12 };

/** The day one step after a day, in calendar months, the day clamped to the month's last. */
export function nextRunAfter(day: string, frequency: RecurringFrequencyValue): string {
  return addMonths(day, MONTHS[frequency]);
}

/** 🔨 A template is ended when it is switched off or its End On is before today; it raises nothing either way. */
export function recurringEnded(template: { isActive: boolean; endOn: string | null }, today: string): boolean {
  return !template.isActive || (template.endOn !== null && template.endOn < today);
}

/** The periods a template raises on a day: each `nextRunOn` up to today and up to its End On, oldest first, at most the run's cap. */
export function duePeriodStarts(template: { isActive: boolean; nextRunOn: string; endOn: string | null; frequency: RecurringFrequencyValue }, today: string): string[] {
  const out: string[] = [];
  if (!template.isActive) return out;
  let day = template.nextRunOn;
  while (day <= today && (template.endOn === null || day <= template.endOn) && out.length < RECURRING_MAX_PERIODS_PER_RUN) {
    out.push(day);
    day = nextRunAfter(day, template.frequency);
  }
  return out;
}
